import { GetServerSidePropsContext } from 'next'
import { parseCookies, setCookie } from 'nookies'

import {
  destroySessionCookie,
  SESSION_COOKIE_OPTIONS
} from 'utils/sessionCookie'

/**
 * SC-10999: multi-contas e multi-empresas.
 *
 * Multi-contas: o mesmo login tem vínculo com várias empresas e troca entre
 * elas pelo menu do topo. Multi-empresas: a visão agregada de um grupo de
 * empresas, com um token de GRUPO que só serve para leitura agregada (listas,
 * KPIs, métricas, busca e relatórios). Toda ação continua usando o token
 * normal da empresa do registro.
 *
 * Este módulo guarda o estado da sessão que não cabe no `nextauth.token`:
 * o token de grupo e o resumo do grupo em cookie (lidos também no SSR) e as
 * listas de empresas/grupos no localStorage.
 */

type Ctx = GetServerSidePropsContext | undefined

export const GROUP_TOKEN_COOKIE = 'nextauth.group_token'
export const GROUP_COOKIE = 'nextauth.group'
export const LOGIN_UUID_COOKIE = 'nextauth.login_uuid'

export type SessionAccount = {
  merchant_uuid: string
  merchant_user_uuid?: string
  name: string
  logo_url?: string | null
  role?: string
  status?: string
}

export type SessionGroupOption = {
  merchant_group_uuid: string
  name: string
  merchant_uuids: string[]
}

export type GroupMerchant = {
  merchant_uuid: string
  uuid?: string
  name: string
  logo_url?: string | null
  // Domínio white-label da fatura da empresa (null quando usa o padrão)
  custom_invoice_domain?: string | null
}

export type SessionGroupPayload = {
  merchant_group_uuid: string
  name: string
  main_merchant_uuid?: string | null
  default_merchant_uuid?: string | null
  merchants?: GroupMerchant[]
  token?: string
  permissions?: string
}

type GroupCookie = {
  merchant_group_uuid: string
  name: string
  main_merchant_uuid?: string | null
  default_merchant_uuid?: string | null
}

export type GroupSession = GroupCookie & {
  merchants: GroupMerchant[]
  isGroupMode: true
}

export type StoredAccounts = {
  accounts: SessionAccount[]
  groups: SessionGroupOption[]
}

const accountsKey = (loginUuid: string) => `cb:accounts:${loginUuid}`
const groupMerchantsKey = (groupUuid: string) => `cb:group:${groupUuid}`

// O nome do grupo pode ter acento: base64 do UTF-8, e não o `encodeBase64`
// do projeto (que decodifica em ascii e corromperia o texto).
const encodeJson = (value: unknown) =>
  Buffer.from(JSON.stringify(value), 'utf8').toString('base64')

const decodeJson = <T>(value?: string): T | null => {
  if (!value) return null

  try {
    return JSON.parse(Buffer.from(value, 'base64').toString('utf8')) as T
  } catch {
    return null
  }
}

const readStorage = <T>(key: string): T | null => {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

const writeStorage = (key: string, value: unknown) => {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // localStorage indisponível (aba privada): o menu busca na API ao abrir.
  }
}

const removeStorage = (key: string) => {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.removeItem(key)
  } catch {
    //
  }
}

export const getContextToken = (ctx?: Ctx) =>
  parseCookies(ctx)['nextauth.token'] as string | undefined

export const getContextMerchantUuid = (ctx?: Ctx) =>
  parseCookies(ctx)['nextauth.uuid'] as string | undefined

export const getGroupToken = (ctx?: Ctx) =>
  parseCookies(ctx)[GROUP_TOKEN_COOKIE] as string | undefined

export const getLoginUuid = (ctx?: Ctx) =>
  parseCookies(ctx)[LOGIN_UUID_COOKIE] as string | undefined

const readGroupCookie = (ctx?: Ctx): GroupCookie | null => {
  const cookies = parseCookies(ctx)
  if (!cookies[GROUP_TOKEN_COOKIE]) return null

  const group = decodeJson<GroupCookie>(cookies[GROUP_COOKIE])
  return group?.merchant_group_uuid ? group : null
}

/** Sessão está na visão multi-empresas (grupo). */
export const isGroupMode = (ctx?: Ctx) => !!readGroupCookie(ctx)

/**
 * Escopo das leituras agregadas: 'group' na visão do grupo, `undefined` fora
 * dela (a chamada segue exatamente como antes).
 */
export const aggregatedScope = (ctx?: Ctx): 'group' | undefined =>
  isGroupMode(ctx) ? 'group' : undefined

export const readGroupSession = (ctx?: Ctx): GroupSession | null => {
  const group = readGroupCookie(ctx)
  if (!group) return null

  const merchants =
    readStorage<GroupMerchant[]>(
      groupMerchantsKey(group.merchant_group_uuid)
    ) || []

  return { ...group, merchants, isGroupMode: true }
}

export const saveGroupMerchants = (
  groupUuid: string,
  merchants: GroupMerchant[]
) => writeStorage(groupMerchantsKey(groupUuid), merchants)

/** Grava a sessão do grupo que veio no payload do login/troca de conta. */
export const persistGroupSession = (group?: SessionGroupPayload | null) => {
  if (!group?.merchant_group_uuid || !group.token) return

  const cookie: GroupCookie = {
    merchant_group_uuid: group.merchant_group_uuid,
    name: group.name,
    main_merchant_uuid: group.main_merchant_uuid,
    default_merchant_uuid: group.default_merchant_uuid
  }

  setCookie(undefined, GROUP_TOKEN_COOKIE, group.token, SESSION_COOKIE_OPTIONS)
  setCookie(undefined, GROUP_COOKIE, encodeJson(cookie), SESSION_COOKIE_OPTIONS)

  saveGroupMerchants(group.merchant_group_uuid, group.merchants || [])
}

export const persistLoginUuid = (loginUuid?: string) => {
  if (!loginUuid) return

  setCookie(undefined, LOGIN_UUID_COOKIE, loginUuid, SESSION_COOKIE_OPTIONS)
}

export const readStoredAccounts = (loginUuid?: string): StoredAccounts => {
  const empty = { accounts: [], groups: [] }
  if (!loginUuid) return empty

  const stored = readStorage<StoredAccounts>(accountsKey(loginUuid))
  const accounts = stored?.accounts
  const groups = stored?.groups

  return {
    accounts: Array.isArray(accounts) ? accounts : [],
    groups: Array.isArray(groups) ? groups : []
  }
}

export const saveStoredAccounts = (
  loginUuid: string | undefined,
  data: Partial<StoredAccounts>
) => {
  if (!loginUuid) return

  writeStorage(accountsKey(loginUuid), {
    accounts: Array.isArray(data.accounts) ? data.accounts : [],
    groups: Array.isArray(data.groups) ? data.groups : []
  })
}

/** Remove os cookies do grupo e do login (chamado pelo destroySession). */
export const destroyGroupCookies = (ctx?: Ctx) => {
  destroySessionCookie(ctx, GROUP_TOKEN_COOKIE)
  destroySessionCookie(ctx, GROUP_COOKIE)
  destroySessionCookie(ctx, LOGIN_UUID_COOKIE)
}

// Resposta da API quando o grupo deixou de valer (plano da empresa principal,
// login com menos de duas empresas...). O token da empresa de contexto segue
// válido, então o usuário volta para ela em vez de ser deslogado.
export const GROUP_UNAVAILABLE_CODE = 'group_unavailable'
const GROUP_UNAVAILABLE_NOTICE = 'cb:group-unavailable-notice'

/** Sai da visão do grupo e recarrega na empresa de contexto, com um aviso. */
export const leaveUnavailableGroup = (message?: string) => {
  if (typeof window === 'undefined') return
  destroyGroupCookies()
  try {
    window.sessionStorage.setItem(
      GROUP_UNAVAILABLE_NOTICE,
      message ||
        'A visão de todas as empresas não está mais disponível. Você voltou para a sua empresa.'
    )
  } catch {
    // sem sessionStorage o aviso só não aparece
  }
  window.location.replace('/visao-geral')
}

/** Aviso guardado pelo leaveUnavailableGroup, lido uma vez após o reload. */
export const takeGroupUnavailableNotice = (): string | null => {
  if (typeof window === 'undefined') return null
  try {
    const notice = window.sessionStorage.getItem(GROUP_UNAVAILABLE_NOTICE)
    if (notice) window.sessionStorage.removeItem(GROUP_UNAVAILABLE_NOTICE)
    return notice
  } catch {
    return null
  }
}

/** Limpa o que foi guardado no navegador para este login (logout). */
export const clearStoredSession = (loginUuid?: string, groupUuid?: string) => {
  if (loginUuid) removeStorage(accountsKey(loginUuid))
  if (groupUuid) removeStorage(groupMerchantsKey(groupUuid))
}

// Rotas que funcionam na visão do grupo. Todo o resto é por empresa e mostra o
// estado de bloqueio com os atalhos para entrar numa empresa do grupo.
const GROUP_ALLOWED_TABS: Record<string, string[] | null> = {
  '/visao-geral': null,
  '/cobrancas': [
    'cobrancas',
    'recorrencias',
    'parcelamentos',
    'nfse',
    'negativacao',
    'transferencias'
  ],
  // Lista de clientes (a ficha é aberta trocando para a empresa do cliente) e
  // os comunicados do portal, só para consulta
  '/clientes': ['clientes', 'comunicacao'],
  // Análise Financeira (MRR, ARR, receitas e churn somados do grupo), contas a
  // pagar e a receber e o fluxo de caixa consolidado
  '/operacao': [
    'analise_financeira',
    'contas-a-receber',
    'contas-a-pagar',
    'fluxo-de-caixa',
    'dre'
  ],
  '/comercial': [
    'contratos',
    'planos',
    'link_de_contratacao',
    'link_de_cadastro'
  ],
  '/contratos/novo-contrato': null,
  // Novidades do produto são as mesmas para todas as empresas
  '/novidades': null,
  '/novidades/[uuid]': null,
  // Detalhe do contrato aberto da lista agregada (busca com a empresa dele)
  '/contratos/[uuid]': null
}

export const isRouteAllowedInGroup = (pathname: string, tab?: string) => {
  if (!(pathname in GROUP_ALLOWED_TABS)) return false

  const tabs = GROUP_ALLOWED_TABS[pathname]
  if (!tabs || !tab) return true

  return tabs.includes(tab)
}

/** Aceita "/cobrancas?tab=recorrencias&..." e diz se a rota vale no grupo. */
export const isPathAllowedInGroup = (path: string) => {
  const [pathname, search = ''] = path.split('?')
  const tab = new URLSearchParams(search).get('tab') || undefined

  return isRouteAllowedInGroup(pathname, tab)
}
